import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { camelCase } from 'lodash';
import cx from 'classnames';
import { Button, ClickAwayListener } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { IconNewLock } from 'src/assets/icons/NewLock';
import { GenerationToolIcon } from 'src/components/GenerationToolIcon';
import { useHistory } from 'src/lib/hooks';
import { useStyles } from './useStyles';

export const SidebarButton = ({
  className,
  classes,
  item,
  active,
  onSubmenuItemClick,
  children,
  onClick,
  activeIcon,
  ...restProps
}) => {
  const styles = useStyles({ classes });
  const [openSecondMenu, setOpenSecondMenu] = useState(false);
  const history = useHistory();

  const StartIcon = (active && activeIcon) || item.icon;

  useEffect(() => {
    if (item.displaySubmenu) {
      setOpenSecondMenu(false);
    }
  }, [item]);

  const handleToggleSubmenu = useCallback(
    (e) => {
      e.preventDefault();
      setOpenSecondMenu((p) => !p);
      onClick?.();
    },
    [onClick]
  );

  const handleSubMenuitemClick = useCallback(
    (item) => {
      setOpenSecondMenu(false);
      onSubmenuItemClick?.(item);
      history.push(item.path);
    },
    [onSubmenuItemClick, history]
  );

  return (
    <div className={styles.wrapper}>
      <Button
        className={cx(styles.root, className, {
          [styles.active]: active,
          [styles.isEmptySubmenuItem]: item.isEmptyItem,
          [styles.isSubmenu]: item.displaySubmenu,
          [styles.menuOpenEndIcon]: openSecondMenu,
          [styles.outlined]: Boolean(item.outlined),
          [styles.narrow]: Boolean(item.narrow),
        })}
        classes={{
          label: styles.label,
          startIcon: styles.startIcon,
          endIcon: styles.endIcon,
        }}
        style={item.style}
        onClick={item.displaySubmenu ? handleToggleSubmenu : onClick}
        startIcon={StartIcon ? <StartIcon className={styles.iconStart} /> : undefined}
        endIcon={item.locked ? <IconNewLock /> : item.endIcon && <item.endIcon />}
        data-testid={`NavigationSidebarLink_${camelCase(item.label)}`}
        data-stonly-id={item.dataStonlyId}
        {...restProps}
      >
        {children || item.label}
      </Button>

      {item.displaySubmenu && openSecondMenu && (
        <div className={cx(styles.secondMenu, styles.secondMenuOverlay)}>
          <ClickAwayListener onClickAway={handleSubMenuitemClick}>
            <div className={styles.secondMenuWrapper}>
              <CloseIcon
                className={styles.submenuCloseButton}
                onClick={handleToggleSubmenu}
              />
              <div className={styles.submenuItemsWrapper}>
                {item.items?.map((subMenuItem) => (
                  <Button
                    key={subMenuItem.id}
                    className={styles.submenuItemButton}
                    onClick={() => handleSubMenuitemClick(subMenuItem)}
                    startIcon={
                      <GenerationToolIcon
                        className={styles.submenuItemIconStart}
                        tool={subMenuItem.key}
                        whiteVariant
                      />
                    }
                  >
                    {subMenuItem.label}
                  </Button>
                ))}
              </div>
            </div>
          </ClickAwayListener>
        </div>
      )}
    </div>
  );
};

SidebarButton.propTypes = {
  className: PropTypes.string,
  item: PropTypes.shape().isRequired,
  active: PropTypes.bool,
  onSubmenuItemClick: PropTypes.func,
};

SidebarButton.defaultProps = {
  className: '',
  active: false,
  onSubmenuItemClick: null,
};
